/**
 * Canonical prediction_status constants (single source of truth, FE side).
 *
 * Reflects the 8 canonical state strings documented in
 * docs/guides/RESULT_DISPATCH_STATES.md (PR #279).
 *
 * Backend counterpart: backend/common/prediction_status.py — manual sync,
 * drift verified via the grep gate documented in the PR description.
 *
 * Vue 2 compatibility: no optional chaining (?.) and no nullish coalescing (??).
 */

// 8 canonical status values (byte-identical to the strings the backend emits).
export const UPLOADING = 'Uploading';
export const PROCESSING = 'Processing';
export const STARTED = 'Started';
export const FINISHED = 'Finished';
export const FINISHED_TRANSMIT = 'Finished(Transmit)';
export const FINISHED_PACS = 'Finished(PACS)';
export const FINISHED_PACS_FAILED = 'Finished(PACS_FAILED)';
export const FAILED = 'Failed';

// Terminal "successfully finished" subset (FAILED excluded).
// Replaces previous indexOf('Finished') substring checks.
export const TERMINAL_FINISHED = [
  FINISHED,
  FINISHED_TRANSMIT,
  FINISHED_PACS,
  FINISHED_PACS_FAILED,
];

// All terminal statuses including FAILED.
export const TERMINAL_PREDICTION_STATUSES = [
  FINISHED,
  FINISHED_TRANSMIT,
  FINISHED_PACS,
  FINISHED_PACS_FAILED,
  FAILED,
];

// In-flight statuses for the FE polling/display "분석 중" indicator.
//
// Intentionally excludes UPLOADING — the External API placeholder stage
// (raw uploaded, decode pending) — because TabularDataset shows uploads
// in a separate UI affordance from "AI 분석 진행 중". This is the FE-side
// counterpart to PredictionStatus.in_flight() in shared/prediction_status.py,
// which DOES include UPLOADING because the BE definition is "any state
// before terminal". The asymmetry is intentional: BE policy vs. FE label.
export const ACTIVE_PREDICTION_STATUSES = [STARTED, PROCESSING];

// Frozen object grouping for callers that prefer namespaced access
// (PredictionStatus.FINISHED_PACS over the bare named export). Mirrors the
// Python StrEnum at shared/prediction_status.py — manual sync, drift gated
// by the strong grep on PACS variants documented in the centralization PR.
export const PredictionStatus = Object.freeze({
  UPLOADING: UPLOADING,
  PROCESSING: PROCESSING,
  STARTED: STARTED,
  FINISHED: FINISHED,
  FINISHED_TRANSMIT: FINISHED_TRANSMIT,
  FINISHED_PACS: FINISHED_PACS,
  FINISHED_PACS_FAILED: FINISHED_PACS_FAILED,
  FAILED: FAILED,
});

// Boundary validator. Use at the wire entry-point (API response handler,
// WebSocket message handler) to surface unknown status strings as errors
// instead of silently flowing them through the UI.
//   const status = parsePredictionStatus(response.prediction_status);
// Throws when ``s`` is not one of the 8 canonical values.
const ALL_VALID_STATUSES = Object.values(PredictionStatus);
export function parsePredictionStatus(s) {
  if (ALL_VALID_STATUSES.indexOf(s) === -1) {
    throw new Error('Unknown prediction_status: ' + JSON.stringify(s));
  }
  return s;
}
